<template>
    <div class="FormField search">

        <div class="FormField__input"
            :class="{ 'suggestions-show': searchResults?.length && (isFocused || isSuggestionsHovered) }">

            <div class="FormField__input__icon Icon">
                <i class="fal fa-search"></i>
            </div>
            <input ref="inputRef" v-model="inputValue" type="text" :name="searchInputName"
                :placeholder="searchInputPlaceholder" @focus="isFocused = true" @blur="isFocused = false"
                @keydown.enter="onEnterPress" autocomplete="off" />
            <div class="FormField__input__reset" @click="onResultReset()">
                <i class="fal fa-times"></i>
            </div>
            <div class="FormField__input__suggestions" @mouseover="isSuggestionsHovered = true"
                @mouseleave="isSuggestionsHovered = false">
                <ul>
                    <li v-for="(result, index) in searchResults" :key="index" @click.prevent="onResultClick(result)">
                        <template v-if="!props.useAsFilter">
                            <a :href="`${locationSearchOverviewPageLink}?locality=${encodeURI(result?.locality)}&lat=${encodeURI(result?.location.lat.toFixed(4))}&lng=${encodeURI(result?.location.lng.toFixed(4))}`"
                                :title="displaySearchResult(result)">
                                {{ displaySearchResult(result) }}
                            </a>
                        </template>
                        <template v-else>
                            <a :title="displaySearchResult(result)">
                                {{ displaySearchResult(result) }}
                            </a>
                        </template>
                    </li>
                </ul>
            </div>

        </div>

        <div v-if="userMessage" class="message message-warning">{{ userMessage }}</div>

    </div>
</template>

<script lang="ts" setup>
const emit = defineEmits(['searchResult']);
const props = withDefaults(defineProps<{
    resultLimit?: number
    useAsFilter?: boolean
}>(), {
    resultLimit: 10,
    useAsFilter: false
})

const { $texts } = useNuxtApp()

const initData: Ref<InitData> = await useInitData()
const { globalConfig } = initData.value
const { locationSearchOverviewPage } = globalConfig
const locationSearchOverviewPageLink = ref(locationSearchOverviewPage?.uri?.path || '/')

const runtimeConfig = useRuntimeConfig()
const gMapApiKey = runtimeConfig.public.geocoder.apiKey
const currentLanguage = useCurrentLanguage()

let defaultInputValue = '';
const route = useRoute();
if (route.query.locality && props.useAsFilter) {
    defaultInputValue = decodeURI(route.query.locality) as string;
}
const inputValue = ref(defaultInputValue)
const inputRef = ref(null)
const isFocused = ref(false)
const isSuggestionsHovered = ref(false)
const searchResults = ref([])
const userMessage = ref('')

const searchInputName = computed(() => $texts('search.name', 'Ansprechperson finden'))
const searchInputPlaceholder = computed(() => $texts('search.placeholder', 'Geben Sie Ihren Standort ein'))

let timeout: ReturnType<typeof setTimeout> | null = null

const fetchGeocodingResults = async () => {
    if (inputValue.value.length < 3) {
        searchResults.value = []
        return
    }

    const econdedInputValue = encodeURI(inputValue.value)

    const geocodingApiUrl = `https://maps.googleapis.com/maps/api/geocode/json?language=${currentLanguage.value}&key=${gMapApiKey}&components=locality:${econdedInputValue}|country:CH`

    const noResultsLabel = $texts('search.no_results', 'Keine Ergebnisse für diesen Standort verfügbar.')
    const data = await $fetch(geocodingApiUrl)

    if (data.status === 'ZERO_RESULTS') {
        userMessage.value = noResultsLabel
        return
    }

    if (data.status !== 'OK') {
        userMessage.value = $texts('search.api_down', 'Es gibt Probleme beim Abrufen der Standorte, bitte versuche es später erneut.')
        if (data?.error_message) {
            console.error('Error fetching geocoding results: ', data.error_message)
        }
        return
    }

    let filteredResults = data?.results.map(result => {

        if (!result?.address_components.some(component =>
            ['sublocality', 'locality', 'postal_code'].some(type => component.types.includes(type))
        )) {
            return
        }

        return {
            sublocality: result?.address_components.find(component =>
                component.types.includes('sublocality')
            )?.long_name ?? '',
            locality: result?.address_components.find(component =>
                component.types.includes('locality')
            )?.long_name ?? '',
            postal_code: result?.address_components.find(component =>
                component.types.includes('postal_code')
            )?.long_name ?? '',
            place_id: result?.place_id ?? '',
            location: result?.geometry?.location,
        }
    }).slice(0, props.resultLimit)

    filteredResults = filteredResults.filter(result => result !== undefined)

    userMessage.value = filteredResults.length < 1 ? $texts('search.no_results', 'Keine Ergebnisse für diesen Standort verfügbar.') : ''
    searchResults.value = filteredResults
}

function onResultReset() {
    inputValue.value = '';
    if (props.useAsFilter) {
        emit('searchResult', null);
    }
}

function emitClickedResult(result) {
    emit('searchResult', result);
}

function displaySearchResult(result) {
    let text = '';
    if (result?.sublocality) text += `${result.sublocality} - `;
    text += result?.locality ?? '';
    if (result?.postal_code && result?.locality) text += ' - ';
    text += result?.postal_code ?? '';
    return text.trim();
}

const isProgrammaticChange = ref(false);

function onResultClick(result) {
    // Temporarily disable the watcher so it doesn't trigger the dropdown again
    isProgrammaticChange.value = true;
    inputValue.value = displaySearchResult(result);

    if (props.useAsFilter) {
        emitClickedResult(result);
    } else {
        window.location.href = `${locationSearchOverviewPageLink.value}?locality=${encodeURI(result?.locality)}&lat=${result?.location?.lat.toFixed(4)}&lng=${result?.location?.lng.toFixed(4)}`
    }

    // Hide suggestions after selecting a result
    searchResults.value = [];

    // Re-enable the watcher after a short delay
    setTimeout(() => {
        isProgrammaticChange.value = false;
    }, 300);
}

function onEnterPress() {
    if (searchResults.value.length === 1) {
        onResultClick(searchResults.value[0]);
    }
}

watch(inputValue, () => {
    if (isProgrammaticChange.value) return;

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
        userMessage.value = '';
        searchResults.value = [];
        fetchGeocodingResults();
    }, 200);
});

defineExpose({
  onResultReset
})
</script>

<style lang="postcss">
.message {
    margin-top: 1rem;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 1em;
    background-color: transparent;

    &.message-warning {
        border-color: var(--color-orange-100, #FF9600);
        background-color: var(--color-orange-10, rgba(255, 150, 0, 0.1));
        color: var(--color-orange-100, #FF9600);
    }
}
</style>
